import { MoreVert } from "@mui/icons-material";
import { Box, IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import { OverviewTableCell, PMButton } from "@pimo/pimo-components";
import { FC, MouseEvent, useState } from "react";

export type OpenAssessmentCellProps = {
  onClick?: () => void;
  isLocked?: boolean;
  tooltip?: string;
  /**
   * Row actions menu, rendered next to the "Open" button. Leave undefined to
   * render no menu at all. PMTable's own `onMenuOpen` menu always draws an
   * "Edit" entry, so the report table hosts its delete-only menu here instead.
   */
  onDelete?: () => void;
  deleteLabel?: string;
};

/** Table cell rendering an "Open" button, used to open the assessment dialog for a row. Disabled while the item is locked by someone else. */
export const OpenAssessmentCell: FC<OpenAssessmentCellProps> = ({
  onClick,
  isLocked,
  tooltip,
  onDelete,
  deleteLabel,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | undefined>();

  const openMenu = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  };
  const closeMenu = () => setMenuAnchor(undefined);

  const menuButton = (
    <IconButton
      size="small"
      aria-label="row actions"
      disabled={isLocked}
      onClick={openMenu}
    >
      <MoreVert fontSize="small" />
    </IconButton>
  );

  return (
    <OverviewTableCell
      sx={{ display: "flex", alignItems: "center", padding: 0 }}
    >
      <Box
        onClick={(event) => event.stopPropagation()}
        sx={{ display: "flex", alignItems: "center" }}
      >
        <PMButton
          size="small"
          variant={isLocked ? "disabled" : "reportBlue"}
          text="Open"
          tooltip={isLocked ? tooltip : undefined}
          onClick={onClick}
        />
        {onDelete && (
          <>
            {isLocked && tooltip ? (
              // Tooltips do not fire on disabled buttons, so wrap it in a span.
              <Tooltip title={tooltip} placement="top">
                <span>{menuButton}</span>
              </Tooltip>
            ) : (
              menuButton
            )}
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={closeMenu}
            >
              <MenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  closeMenu();
                  onDelete();
                }}
              >
                {deleteLabel || "Delete"}
              </MenuItem>
            </Menu>
          </>
        )}
      </Box>
    </OverviewTableCell>
  );
};
